import React, { useState, useContext, useEffect } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Stack,
  Box,
  Group,
  Loader,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { DataContext } from "../../../../context/DataContext";
import useLocale from "../../../../hooks/useLocale";
import FormField from "../../../../components/form-field";
import DateOfBirthSelect from "../../../../components/date-of-birth-select";
import useStatusCheck from "../../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../../../../hooks/useAdjustInputSize";
import { getEdgeQueueUrl } from "../../../../utils/edge";

export default function RjSsnVerification() {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("rj");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(2);
  const [edgeStatusPolling, setEdgeStatusPolling] = useState(false);
  const inputSize = useAdjustInputSize();

  // Edge mode detection
  const isEdgeMode = data?.edge === true;
  console.log('[RJ SSN Verification] Edge mode check:', { isEdgeMode, dataEdge: data?.edge });

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  const componentKey = "idmatch/rj/ssn/verification";

  // The RJ identity-prefill service (IDMatch5.0.RJRT.Custom) rejects a body
  // with no DateOfBirth (400 QRY1 "Service requires DateOfBirth"). Upstream
  // the phone step runs `nameAndPhoneOnly`, so it never asks for a DOB — the
  // backend normally fills the gap from the cached IdentityPrefill result.
  // When the phone check fails outright (unroutable number, no SMS delivery)
  // prefill resolves nothing, and the submit is guaranteed to error out. So
  // collect the DOB here whenever the journey doesn't already carry one.
  //
  // Frozen on mount: submitHandler writes form.values.dob back into `data`,
  // which would otherwise flip this false and unmount the field mid-submit.
  const [needsDob] = useState(() => !data.dob);

  // Form configuration for RJ SSN verification
  const formConfig = {
    fields: [
      {
        name: "ssn",
        label: locale?.general?.lastFourSSN || text.general.lastFourSSN,
        initialValue: "",
        isMaskedInput: true,
        type: "tel",
        maxLength: 4,
        pattern: "[0-9]*",
        inputMode: "numeric",
        validation: (value) => {
          if (/[^0-9]/.test(value)) {
            return (
              locale?.validation?.ssnOnlyNumbers ||
              "SSN must contain only numbers"
            );
          }
          if (value.length !== 4) {
            return (
              locale?.general?.lastFourSSNError || text.general.lastFourSSNError
            );
          }
          return null;
        },
      },
    ],
  };

  // Status check handler
  const handleStatusResult = (statusResult) => {
    if (statusResult.status === "PASS") {
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      setLoading(true);
      return true;
    }

    if (statusResult.status === "FAIL") {
      dispatch({ type: "SET_STATUS", payload: "FAIL" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      setLoading(true);
      return true;
    }

    return false;
  };

  // Use appropriate status check based on mode
  useStatusCheck(!isEdgeMode && result, false, componentKey, handleStatusResult);
  useEdgeStatusCheck(isEdgeMode && edgeStatusPolling);

  // Same rule as the phonematch step: YYYYMMDD, 19xx/20xx, and a real
  // calendar date (rejects 20260231 and friends).
  //
  // Empty and invalid are deliberately different messages. DateOfBirthSelect
  // only lifts a value once all 8 digits are entered, so "" means the field is
  // unfinished and the server's "…is required" copy fits. A complete but
  // impossible date (02/31/1990) is a different failure — reusing "required"
  // there tells the user to fill in a field that visibly already holds a date.
  const validateDob = (value) => {
    const requiredError =
      locale?.general?.dateOfBirthError || text.general.dateOfBirthError;
    const invalidError =
      locale?.general?.dateOfBirthInvalid || "Please enter a valid date of birth";

    if (!value) {
      return requiredError;
    }

    const dobPattern =
      /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
    if (!dobPattern.test(value)) {
      return invalidError;
    }
    const year = parseInt(value.substring(0, 4), 10);
    const month = parseInt(value.substring(4, 6), 10) - 1;
    const day = parseInt(value.substring(6, 8), 10);
    const date = new Date(year, month, day);
    const isRealDate =
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day;
    return isRealDate ? null : invalidError;
  };

  // Form setup
  const form = useForm({
    initialValues: {
      ssn: data.ssn || "",
      dob: data.dob || "",
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      formConfig.fields.forEach((field) => {
        if (field.validation) {
          const error = field.validation(values[field.name]);
          if (error) {
            errors[field.name] = error;
          }
        }
      });
      if (needsDob) {
        const dobError = validateDob(values.dob);
        if (dobError) {
          errors.dob = dobError;
        }
      }
      return errors;
    },
  });

  const handleDobChange = (dob) => {
    form.setFieldValue("dob", dob.year + dob.month + dob.day);
  };

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    data.ssn = form.values.ssn;
    if (needsDob) {
      data.dob = form.values.dob;
    }
    data.attemptsLeft = newAttemptsLeft;
    data.isContinue = false; // This is a new verification attempt, not a continuation
    data.isOtpInput = false; // This is data collection, not OTP input

    try {
      // Determine URL and request data based on edge mode
      const apiUrl = isEdgeMode
        ? getEdgeQueueUrl()
        : process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/phonematch";

      const requestData = isEdgeMode
        ? { ...data, queue: "phonematch" }
        : data;

      console.log('[RJ SSN Verification] Submitting to:', apiUrl, { isEdgeMode });

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      const responseData = await response.json();

      if (isEdgeMode) {
        // Edge mode: queue accepted, immediately start edge status check
        const reqId = responseData.request_id || settings.request_id;
        if (responseData.request_id) {
          dispatch({ type: "SET_SETTINGS", payload: { ...settings, request_id: responseData.request_id } });
        }

        console.log('[RJ SSN Verification] Edge queue response:', { reqId, responseData });

        // Start edge status polling immediately
        setIsVerifying(false);
        setLoading(true);
        setEdgeStatusPolling(true);
        return;
      }

      // Non-edge mode: handle synchronous response
      dispatch({ type: "SET_STATUS", payload: "PENDING" });
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });

      setIsVerifying(false);
      setLoading(true);
      setResult(true);
    } catch (error) {
      console.error('[RJ SSN Verification] Submit error:', error);
      alert("Something went wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  if (loading && !isVerifying) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          height: "100%",
        }}
      >
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box style={{ flex: 1, width: "100%" }}>
            {/* Custom header without icon */}
            <Box mb="md">
              <Text fz={inputSize === "md" ? 24 : 22} fw={700} mb="xs">
                Unable to confirm your age
              </Text>
              <Text c="dimmed" fz={inputSize === "md" ? 15 : 14} mb="lg">
                {needsDob
                  ? "This happens sometimes and you can still get age verified. Let's try with your date of birth and the last 4 digits of your SSN."
                  : "This happens sometimes and you can still get age verified. Let's try with the last 4 digits of your SSN."}
              </Text>
            </Box>

            <Stack spacing="sm">
              {needsDob && (
                <DateOfBirthSelect
                  isSinglePageForm={true}
                  onDobChange={handleDobChange}
                  {...form.getInputProps("dob")}
                />
              )}
              {formConfig.fields.map((field) => (
                <FormField
                  key={field.name}
                  field={field}
                  form={form}
                  settings={settings}
                  componentKey={componentKey}
                  showMoreFields={true}
                  isSinglePageForm={true}
                />
              ))}
            </Stack>
          </Box>
          <Box mt="md" style={{ width: "100%" }}>
            <Button
              fullWidth
              size="lg"
              type="submit"
              disabled={isVerifying || !form.isValid()}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.01)",
                },
                '&[data-loading="true"]': {
                  color: buttonContentColor,
                },
              }}
            >
              {isVerifying ? (
                <Group spacing="xs" noWrap>
                  <Loader size="xs" color={theme.primaryColor} />
                  <Text c="inherit">Verifying...</Text>
                </Group>
              ) : (
                "Continue"
              )}
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
}
